<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type { ValidData } from '@/components/forms/JobEditCustomerForm.vue';
import type { CustomerItem } from '@/models/jobEdit';

import { computed, ref, watch } from 'vue';

import JobEditCustomerForm from '@/components/forms/JobEditCustomerForm.vue';
import JobEditAccordionSection from '@/components/sections/JobEditAccordionSection.vue';
import { useJobEditCustomer } from '@/composables/jobEdit/useJobEditCustomer';
import { createEmptyCustomerItem } from '@/models/jobEdit';

export interface Props {
  initialData?: CustomerItem;
}

const props = withDefaults(defineProps<Props>(), {
  initialData: createEmptyCustomerItem,
});

const emit = defineEmits<{
  submit: [
    event: SubmitEventPromise | null | undefined,
    customerData: ValidData,
  ];
}>();

const {
  initialData,
  isEditing,
  isLoading,
  startEditing,
  saveEditing,
  cancelEditing,
  setFormRef,
  setCurrentFormData,
} = useJobEditCustomer(() => props.initialData);

const customerFormRef = ref<InstanceType<typeof JobEditCustomerForm> | null>(
  null,
);

watch(
  () => customerFormRef.value?.form,
  (formElement) => {
    setFormRef(formElement ?? null);
  },
  { immediate: true },
);

const companyName = computed(() => {
  return props.initialData.customer_code || '';
});

async function handleFormSubmit(
  event: SubmitEventPromise | null | undefined,
  validData: ValidData,
): Promise<void> {
  try {
    setCurrentFormData(validData);
    await saveEditing();
    emit('submit', event, validData);
  } catch {
    // Error is already handled: fetcher logs to Sentry + shows notification,
    // useJobEditSectionBase opens a "conflict" dialog
  }
}

async function handleCancel(): Promise<void> {
  await cancelEditing();
}

function handleSave(): void {
  // requestSubmit triggers the form's native submit, which v-form's
  // validate-on="submit lazy" handles internally before emitting submit.
  customerFormRef.value?.form?.requestSubmit();
}
</script>

<template>
  <JobEditAccordionSection
    :class="[$style.jobEditCustomerSection, { [$style.editing]: isEditing }]"
    title="CUSTOMER DETAILS"
    :is-editing="isEditing"
    :is-loading="isLoading"
    editable
    data-testid="jobEditCustomerSection"
    @cancel="handleCancel"
    @save="handleSave"
    @edit-btn-click="startEditing"
  >
    <template v-if="companyName" #collapsedContent>
      <span>{{ companyName }}</span>
    </template>

    <div :class="$style.content">
      <JobEditCustomerForm
        ref="customerFormRef"
        :initial-data="initialData"
        :readonly="!isEditing"
        @submit="handleFormSubmit"
      />
    </div>
  </JobEditAccordionSection>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

.jobEditCustomerSection {
  max-width: 930px;
  margin-bottom: 24px;
  transition: all 0.3s ease;
  position: relative;
}

.editing {
  border-color: colors.$color-brand-500;
  box-shadow: 0 0 0 2px colors.$color-brand-100;
  background-color: colors.$color-white;
  z-index: 100;
}

.content {
  padding: 16px;
  overflow: hidden;
}
</style>
